import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
  OnInit,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GenderUtils } from '../../../utils/gender-utils';
import { PhoneInputComponent } from '../phone-input/phone-input.component';
import { validateAtLeastOneMethodSelectedPerType } from '../../shared/validators/validators';
@Component({
  selector: 'app-patient-information-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, PhoneInputComponent],
  templateUrl: './patient-information-form.component.html',
  styleUrl: './patient-information-form.component.scss',
})
export class PatientInformationFormComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @Input() formGroup: FormGroup;
  @Input() maxDate: string;
  @Input() newPatientId: string;
  @Input() lockTckn: boolean = false;

  @Output() preventForbiddenCharactersEvent = new EventEmitter<{
    event: any;
    type: string;
  }>();
  @Output() nextStepEvent = new EventEmitter<void>();
  @Output() ecgFileUploadedEvent = new EventEmitter<{
    fileData: string | ArrayBuffer | null;
    fileName: string;
  }>();
  @Output() bmiCalculated = new EventEmitter<number>();

  constructor(private translate: TranslateService, private fb: FormBuilder,) { }

  genders = GenderUtils.getAllGenders();
  uploadedFile: string | ArrayBuffer | null = null;
  isUploading: boolean = false;
  uploadFileName: string = '';
  notificationTypes: { id: string; label: string }[] = [
    { id: 'lowBloodSugar', label: this.translate.instant('lowBloodSugar') },
    {
      id: 'noGlucoseMeasurement',
      label: this.translate.instant('noGlucoseMeasurement'),
    },
    {
      id: 'noSurveyData',
      label: this.translate.instant('noSurveyData'),
    },
  ];
  notificationMethods: { id: string; label: string }[] = [
    { id: 'doctorSms', label: this.translate.instant('doctorSms') },
    { id: 'doctorEmail', label: this.translate.instant('doctorEmailNotice') },
    { id: 'nextOfKinSms', label: this.translate.instant('nextOfKinSms') },
    { id: 'nextOfKinEmail', label: this.translate.instant('nextOfKinEmail') },
  ];


  get nextOfKins(): FormArray {
    return this.formGroup.get('patientRelatives') as FormArray;
  }

  get isTcknReadonly(): boolean {
    return this.lockTckn || !!this.newPatientId?.length;
  }
  preventForbiddenCharacters(event: any, type: string): void {
    this.preventForbiddenCharactersEvent.emit({ event, type });
  }

  checkAllNotificationSelectionsAreRequired(): boolean {
    if (!this.formGroup) return false;

    return Object.keys(this.formGroup.controls)
      .filter(key => key.startsWith('notification_'))
      .some(key => {
        const control = this.formGroup.get(key);
        return control?.invalid && control?.touched;
      });
  }
  isControlInvalidAt(index: number, controlName: string): boolean {
    const kinGroup = this.nextOfKins.at(index) as FormGroup;
    const control = kinGroup.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
  goToNextStep(): void {
    //console.log(this.formGroup.value);
    this.nextStepEvent.emit();
  }

  isControlInvalid(controlName: string): boolean {
    if (!this.formGroup) return false;
    const control = this.formGroup.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  calculateBMI(): number {
    const weight = this.formGroup.get('weight')?.value;
    const height = this.formGroup.get('height')?.value;
    if (!weight || !height) return 0;

    const bmi: number = +(weight / Math.pow(height / 100, 2)).toFixed(2);
    this.bmiCalculated.emit(bmi);
    return bmi;
  }

  onDateInputChange(value: string): void {
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    const today = new Date();
    let formattedValue = '';

    if (sanitizedValue.length <= 2) {
      formattedValue = sanitizedValue;
    } else if (sanitizedValue.length <= 4) {
      formattedValue = `${sanitizedValue.slice(0, 2)}-${sanitizedValue.slice(
        2
      )}`;
    } else if (sanitizedValue.length <= 8) {
      formattedValue = `${sanitizedValue.slice(0, 2)}-${sanitizedValue.slice(
        2,
        4
      )}-${sanitizedValue.slice(4)}`;
    }

    const [day, month, year] = formattedValue
      .split('-')
      .map((part) => parseInt(part, 10));

    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 145;
    if (
      formattedValue.startsWith('00') ||
      formattedValue.substring(3, 5) === '00' ||
      formattedValue.substring(6, 7) === '0' ||
      (day && day > 31) ||
      (month && month > 12) ||
      (year &&
        year.toString().length == 4 &&
        (year > currentYear || year < minYear))
    ) {
      this.formGroup.patchValue({
        birthDate: '',
      });
      return;
    }

    const inputDate = new Date(year, month - 1, day);

    const todayFormatted = `${today.getDate().toString().padStart(2, '0')}-${(
      today.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}-${today.getFullYear()}`;

    if (sanitizedValue.length === 8 && inputDate > today) {
      this.formGroup.patchValue({
        birthDate: todayFormatted,
      });
    } else {
      this.formGroup.patchValue({
        birthDate: formattedValue,
      });
    }
  }

  onDragDropAreaClick() {
    this.fileInput.nativeElement.click();
  }

  onRemoveFile() {
    this.formGroup.patchValue({
      ecgFile: null,
    });
    this.uploadedFile = null;
  }

  onECGFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      const fileName = file.name.toLowerCase();
      if (fileName.endsWith('.ecg')) {
        this.isUploading = true;
        if (input.files && input.files.length > 0) {
          this.handleECGFile(input.files[0]);
        }
      }
    }
  }

  handleECGFile(file: File) {
    const reader = new FileReader();
    this.uploadFileName = file?.name;
    reader.onload = () => {
      this.uploadedFile = reader.result;
      this.ecgFileUploadedEvent.emit({
        fileData: this.uploadedFile,
        fileName: this.uploadFileName,
      });
    };
    this.isUploading = false;
    reader.readAsDataURL(file);
  }
}
